import { CalendarClock, TrendingUp, TrendingDown, CheckCircle } from 'lucide-react';
import { clsx } from 'clsx';
import { useTranslation } from 'react-i18next';
import type { RecurringForecast } from '../../types';

interface RecurringForecastCardProps {
  forecast: RecurringForecast;
  formatCurrency: (amount: number) => string;
  noCard?: boolean;
}

export default function RecurringForecastCard({ forecast, formatCurrency, noCard }: RecurringForecastCardProps) {
  const { t } = useTranslation('dashboard');
  const hasRecurring = forecast.expectedIncome > 0 || forecast.expectedExpenses > 0;

  if (!hasRecurring) {
    const emptyContent = (
      <>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">{t('recurringForecastDetails.monthForecast')}</h3>
          <div className="p-2 rounded-lg bg-purple-100">
            <CalendarClock className="w-5 h-5 text-purple-600" />
          </div>
        </div>
        <div className="flex flex-col items-center justify-center h-32 text-gray-500">
          <CalendarClock className="w-10 h-10 mb-2 opacity-50" />
          <p className="text-sm text-center">{t('recurringForecastDetails.noRecurringConfigured')}</p>
        </div>
      </>
    );
    if (noCard) return emptyContent;
    return <div className="card h-full">{emptyContent}</div>;
  }

  const mainContent = (
    <>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">{t('recurringForecastDetails.monthForecast')}</h3>
        <div className="p-2 rounded-lg bg-purple-100">
          <CalendarClock className="w-5 h-5 text-purple-600" />
        </div>
      </div>

      {/* Barre de progression */}
      <div className="mb-4">
        <div className="flex justify-between text-sm text-gray-600 mb-1">
          <span>{t('recurringForecastDetails.progress')}</span>
          <span className="font-medium">{forecast.progressPercent.toFixed(0)}%</span>
        </div>
        <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
          <div
            className="h-full bg-purple-500 rounded-full transition-all duration-500"
            style={{ width: `${forecast.progressPercent}%` }}
          />
        </div>
      </div>

      {/* Détails revenus */}
      <div className="space-y-3">
        <div className="p-3 bg-green-50 rounded-lg">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-green-600" />
              <span className="text-sm font-medium text-green-700">{t('recurringForecastDetails.recurringIncome')}</span>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2 text-sm">
            <div>
              <p className="text-gray-500">{t('recurringForecastDetails.expected')}</p>
              <p className="font-medium text-gray-900">{formatCurrency(forecast.expectedIncome)}</p>
            </div>
            <div>
              <p className="text-gray-500">{t('recurringForecastDetails.validated')}</p>
              <p className="font-medium text-green-600 flex items-center gap-1">
                {formatCurrency(forecast.actualIncome)}
                {forecast.actualIncome >= forecast.expectedIncome && forecast.expectedIncome > 0 && (
                  <CheckCircle className="w-4 h-4" />
                )}
              </p>
            </div>
          </div>
        </div>

        {/* Détails dépenses */}
        <div className="p-3 bg-red-50 rounded-lg">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <TrendingDown className="w-4 h-4 text-red-600" />
              <span className="text-sm font-medium text-red-700">{t('recurringForecastDetails.recurringExpenses')}</span>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2 text-sm">
            <div>
              <p className="text-gray-500">{t('recurringForecastDetails.expected')}</p>
              <p className="font-medium text-gray-900">{formatCurrency(forecast.expectedExpenses)}</p>
            </div>
            <div>
              <p className="text-gray-500">{t('recurringForecastDetails.validated')}</p>
              <p className={clsx(
                'font-medium flex items-center gap-1',
                forecast.actualExpenses >= forecast.expectedExpenses ? 'text-red-600' : 'text-gray-900'
              )}>
                {formatCurrency(forecast.actualExpenses)}
                {forecast.actualExpenses >= forecast.expectedExpenses && forecast.expectedExpenses > 0 && (
                  <CheckCircle className="w-4 h-4 text-red-600" />
                )}
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );

  if (noCard) return mainContent;
  return <div className="card h-full">{mainContent}</div>;
}
